import { existsSync, promises as fsp } from 'node:fs';
import path from 'node:path';
import { consola } from 'consola';
import { matchMonocerosFeature } from '../util/ref.js';
import { hasDeployBriefing } from '../briefing/deploy-md.js';
import type { CreateOptions } from './types.js';

/**
 * Hosted providers that authenticate with a single API key, written
 * straight to `provider.<id>.options.apiKey`. This is a heuristic
 * allowlist, not an exhaustive registry: it gates the "just an apiKey"
 * shortcut so a typo'd or local provider name (e.g. `ollama`) doesn't
 * silently produce a half-baked provider block. Anything not here needs
 * the explicit custom-provider path (`npm` + `baseUrl`).
 */
const KNOWN_APIKEY_PROVIDERS = [
  'anthropic',
  'openai',
  'google',
  'openrouter',
  'mistral',
  'groq',
  'deepseek',
  'xai',
] as const;

/**
 * Derive the OpenCode provider id from a `provider/model-id` model
 * string — the segment before the first `/`. Returns `undefined` when
 * the model is empty or has no provider prefix (e.g. a bare model id).
 *
 *   anthropic/claude-sonnet-4-6 → anthropic
 *   openai/gpt-4o-mini          → openai
 *   ''                          → undefined
 *   claude-sonnet-4-6           → undefined
 */
export function deriveOpencodeProvider(model: string): string | undefined {
  return parseOpencodeModel(model)?.provider;
}

/**
 * Split a `provider/model-id` string into its parts. The provider is
 * everything before the first `/`; the model id is everything after
 * (it may itself contain slashes, e.g. `lmstudio/google/gemma-3n`).
 * Returns `undefined` when there is no provider prefix.
 */
export function parseOpencodeModel(
  model: string,
): { provider: string; modelId: string } | undefined {
  const idx = model.indexOf('/');
  if (idx <= 0 || idx === model.length - 1) return undefined;
  return { provider: model.slice(0, idx), modelId: model.slice(idx + 1) };
}

/**
 * Write OpenCode's global config into the container's
 * `home/.config/opencode/opencode.json`, derived from the opencode
 * feature's yml options. No-op when the container has no opencode
 * feature.
 *
 * Written at **apply** (here in the scaffold), NOT baked into the
 * feature's cached image layer — so a change to the yml takes effect on
 * the next apply instead of being frozen by the layer cache (same
 * rationale as `writeClaudePermissionMode`, ADR 0018). The write
 * **merges**: keys the user (or OpenCode) put in opencode.json are
 * preserved; only the keys Monoceros manages are touched.
 *
 * Two provider modes, switched by whether the `npm` option is set:
 *
 *   - **Hosted (`npm` empty)** — the common case. `model:
 *     anthropic/claude-sonnet-4-6` + `apiToken` →
 *     `provider.anthropic.options.apiKey`. The provider must be a known
 *     single-key provider (see `KNOWN_APIKEY_PROVIDERS`); an unknown one
 *     warns instead of writing a broken block. No token = interactive /
 *     env auth, nothing written.
 *   - **Custom (`npm` set)** — local or self-hosted, e.g. Ollama.
 *     `model: ollama/llama3` + `npm: @ai-sdk/openai-compatible` +
 *     `baseUrl: http://ollama:11434/v1` → a full provider block,
 *     including the single-entry `models` map synthesized from the model
 *     id. `apiToken` (if set) becomes `options.apiKey` (some proxies
 *     need it; local Ollama does not).
 *
 * `instructions` (AGENTS.md plus the chapters it imports —
 * `.monoceros/conventions.md`, `.monoceros/servers.md`,
 * `.monoceros/commands.md`, absolute workspace paths) is always written so
 * the briefing loads regardless of auth mode; `.monoceros/deploy.md` joins
 * it when the container has services that carry a pipeline shape. Claude
 * Code reaches those files through the `@`-imports in AGENTS.md — OpenCode
 * does not follow those, so it needs them listed here or the agent works
 * from a briefing with holes in it.
 * `permission.external_directory` follows the mount: the workspace is
 * pre-allowed (it is the agent's working area, and the briefing files the
 * `instructions` above point at live at its root), with `home/`, `data/`,
 * `.devcontainer/` and `.monoceros/git-credentials` denied back afterwards -
 * last matching rule wins.
 */
export async function writeOpencodeConfig(
  targetDir: string,
  containerName: string,
  features: CreateOptions['features'],
  services: CreateOptions['services'] = [],
): Promise<void> {
  if (!features) return;
  const entry = Object.entries(features).find(
    ([ref]) => matchMonocerosFeature(ref)?.name === 'opencode',
  );
  if (!entry) return; // no opencode feature → nothing to configure

  const options = entry[1] ?? {};
  const str = (key: string): string =>
    typeof options[key] === 'string' ? (options[key] as string).trim() : '';
  const model = str('model');
  const apiToken = str('apiToken');
  const npm = str('npm');
  const baseUrl = str('baseUrl');
  const theme = str('theme');
  const lsp = options.lsp === true || options.lsp === 'true';

  const file = path.join(
    targetDir,
    'home',
    '.config',
    'opencode',
    'opencode.json',
  );
  await fsp.mkdir(path.dirname(file), { recursive: true });

  let config: Record<string, unknown> = {};
  if (existsSync(file)) {
    try {
      const txt = await fsp.readFile(file, 'utf8');
      if (txt.trim()) {
        const parsed: unknown = JSON.parse(txt);
        if (typeof parsed === 'object' && parsed !== null) {
          config = parsed as Record<string, unknown>;
        }
      }
    } catch {
      // Malformed opencode.json — start clean rather than failing the
      // apply; we only own a few keys.
      config = {};
    }
  }

  if (typeof config.$schema !== 'string') {
    config.$schema = 'https://opencode.ai/config.json';
  }

  // Briefing: ensure both managed instruction files are present, keeping
  // any the user added. Absolute paths against the in-container workspace
  // root so OpenCode loads them regardless of the session's cwd.
  const workspaceRoot = `/workspaces/${containerName}`;
  const managedInstructions = [
    `${workspaceRoot}/AGENTS.md`,
    `${workspaceRoot}/.monoceros/conventions.md`,
    `${workspaceRoot}/.monoceros/servers.md`,
    `${workspaceRoot}/.monoceros/commands.md`,
    ...(hasDeployBriefing(services)
      ? [`${workspaceRoot}/.monoceros/deploy.md`]
      : []),
  ];
  // Filter the user's list against every path we may ever manage, not just
  // the active ones: when the last service leaves the yml, deploy.md is
  // deleted, and a leftover entry would point OpenCode at a missing file.
  const everManaged = [
    `${workspaceRoot}/AGENTS.md`,
    `${workspaceRoot}/.monoceros/conventions.md`,
    `${workspaceRoot}/.monoceros/servers.md`,
    `${workspaceRoot}/.monoceros/commands.md`,
    `${workspaceRoot}/.monoceros/deploy.md`,
  ];
  const existingInstructions = Array.isArray(config.instructions)
    ? (config.instructions as unknown[]).filter(
        (i): i is string => typeof i === 'string',
      )
    : [];
  config.instructions = [
    ...managedInstructions,
    ...existingInstructions.filter((i) => !everManaged.includes(i)),
  ];

  // Pre-approve the workspace paths the Monoceros briefing tells the agent to
  // use. OpenCode's `external_directory` permission defaults to "ask" and fires
  // when a tool touches a path outside the working directory (the cwd, checked
  // via `Filesystem.contains`), so launched from a project subdir the agent
  // gets prompted on its first access to these. The briefing directs it to:
  // build under `projects/`, register new projects in `<name>.code-workspace`,
  // and write detached-server PIDs/logs under `logs/`. We allow exactly those,
  // nothing more: `home/` (provider key, .claude.json), `data/`,
  // `.devcontainer/` and `.monoceros/git-credentials` stay gated. OpenCode's
  // wildcard turns `*` into the regex `.*`, which spans `/`, so a single star
  // covers every depth (`**` is not a special token there). Skipped when the
  // user set a string-valued `permission` policy, which we leave alone.
  if (typeof config.permission !== 'string') {
    const permission =
      typeof config.permission === 'object' && config.permission !== null
        ? (config.permission as Record<string, unknown>)
        : {};
    const ext =
      typeof permission.external_directory === 'object' &&
      permission.external_directory !== null
        ? (permission.external_directory as Record<string, unknown>)
        : {};
    // The whole workspace, then the few things inside it that stay closed.
    //
    // It used to be `projects/*`, the code-workspace file and `logs/*`, which
    // left the agent asking for the workbench's own briefing: every path in
    // `instructions` above sits at the workspace root, so an agent launched
    // from a project subdir is outside all of them, and the same function that
    // points it at those files never allowed them. Listing the briefing files
    // one by one fixed that case and left the next one: `ls /workspaces/<name>`
    // still prompts, and so does every path a builder adds later.
    //
    // So the rule follows the actual boundary. The workspace is the agent's
    // working area - that is what the mount is for. What must not leak is
    // narrower than a directory listing and is denied back below, after the
    // allow, because OpenCode resolves last-match-wins.
    for (const p of [
      `${workspaceRoot}/*`,
      `${workspaceRoot}/${containerName}.code-workspace`,
    ]) {
      ext[p] = 'allow';
    }
    // `home/` holds the provider keys and `.claude.json`, `data/` the service
    // volumes' contents, `.devcontainer/` the generated build inputs (feature
    // options include tokens), and the git credentials are a password file.
    // None of them is something an agent needs to read to do its work.
    for (const p of [
      `${workspaceRoot}/home/*`,
      `${workspaceRoot}/data/*`,
      `${workspaceRoot}/.devcontainer/*`,
      `${workspaceRoot}/.monoceros/git-credentials`,
    ]) {
      ext[p] = 'deny';
    }
    permission.external_directory = ext;
    config.permission = permission;
  }

  // Model: only when set. An empty option must not clobber a user-set
  // model, and leaving it unset lets OpenCode prompt on first run.
  if (model) {
    config.model = model;
  }

  const parsed = parseOpencodeModel(model);

  if (npm) {
    // Custom-provider mode: build the full block (npm + baseURL + the
    // single-entry models map synthesized from the model id).
    if (!parsed) {
      consola.warn(
        '[opencode] `npm` is set but `model` is empty or has no provider prefix — set `model: <provider>/<model-id>` to configure a custom provider.',
      );
    } else {
      writeCustomProvider(config, parsed, { npm, baseUrl, apiToken });
    }
  } else if (parsed) {
    // Hosted-provider mode.
    if (
      (KNOWN_APIKEY_PROVIDERS as readonly string[]).includes(parsed.provider)
    ) {
      // A token is optional: without one, the provider authenticates via
      // its standard env var or an interactive `opencode auth login`.
      if (apiToken) {
        writeHostedApiKey(config, parsed.provider, apiToken);
      }
    } else {
      consola.warn(
        `[opencode] '${parsed.provider}' is not a known single-key provider (${KNOWN_APIKEY_PROVIDERS.join(', ')}). For a custom or local provider (e.g. Ollama), set the \`npm\` and \`baseUrl\` options on the opencode feature.`,
      );
    }
  }

  writeLspServers(config, lsp);

  await fsp.writeFile(file, `${JSON.stringify(config, null, 2)}\n`);
  await writeTuiConfig(path.dirname(file), theme);
}

/**
 * Language servers feed diagnostics back to the agent right after an edit,
 * instead of it finding out when the acceptance command runs. OpenCode
 * ships them off, so the option carries the switch.
 *
 * The switch alone would do nothing for TypeScript and JavaScript, which is
 * most of what people write here. OpenCode's built-in `typescript` server
 * needs `typescript/lib/tsserver.js`, and the runtime image's base
 * (`devcontainers/typescript-node`) brings TypeScript 7, the native port,
 * which has no `tsserver.js` at all - `tsc` is a shim around a binary. The
 * server then fails to start, silently, and `lsp: true` looks broken.
 *
 * So the switch also registers that binary as a server of its own: TS 7
 * speaks LSP directly via `tsc --lsp --stdio`. The command is bare, not a
 * path, because `tsc` resolves to the image's global TypeScript in every
 * container.
 *
 * The built-in one is disabled at the same time, and that part is not
 * belt-and-braces: in a project that pins TypeScript 5 both would run, the
 * built-in on the project's own version and ours on the image's, and the
 * same file would be diagnosed twice by two checkers. A project that wants
 * its own version instead can say so in its `.opencode/opencode.json`,
 * which wins over this one.
 *
 * All of it goes away when OpenCode supports the TS 7 layout: delete the
 * two keys and the plain `lsp: true` is enough.
 *
 * Only those two keys are ours. Anything else a builder put under `lsp`
 * stays, and switching the option off removes ours again rather than
 * leaving a config nothing declares.
 */
function writeLspServers(config: Record<string, unknown>, enabled: boolean) {
  const existing =
    typeof config.lsp === 'object' && config.lsp !== null
      ? (config.lsp as Record<string, unknown>)
      : {};
  if (!enabled) {
    delete existing.typescript;
    delete existing.tsgo;
    if (Object.keys(existing).length > 0) config.lsp = existing;
    else delete config.lsp;
    return;
  }
  existing.typescript = { disabled: true };
  existing.tsgo = {
    command: ['tsc', '--lsp', '--stdio'],
    extensions: ['.ts', '.tsx', '.js', '.jsx', '.mjs', '.cjs', '.mts', '.cts'],
  };
  config.lsp = existing;
}

/**
 * The TUI half of the config, `tui.json`, which is where OpenCode reads
 * the theme from. Only the `theme` key is ours: the file also carries
 * the sidebar, the diff style and the notification settings, and those
 * stay whatever the builder made them.
 *
 * An empty option removes the key rather than leaving the last value in
 * place, so the yml stays the source of truth for it. The `/theme`
 * picker is unaffected either way: it writes the live choice to
 * `~/.local/state/opencode/kv.json`, which is state and not config.
 */
async function writeTuiConfig(dir: string, theme: string): Promise<void> {
  const file = path.join(dir, 'tui.json');
  let config: Record<string, unknown> = {};
  if (existsSync(file)) {
    try {
      const txt = await fsp.readFile(file, 'utf8');
      if (txt.trim()) {
        const parsed: unknown = JSON.parse(txt);
        if (typeof parsed === 'object' && parsed !== null) {
          config = parsed as Record<string, unknown>;
        }
      }
    } catch {
      // Unreadable or invalid JSON: same rule as opencode.json, start
      // from scratch rather than refusing to apply.
      config = {};
    }
  }
  if (theme) {
    config.$schema = 'https://opencode.ai/tui.json';
    config.theme = theme;
  } else {
    delete config.theme;
    // Nothing of ours left and nothing of the builder's either: do not
    // leave an empty file behind.
    if (Object.keys(config).filter((k) => k !== '$schema').length === 0) {
      if (existsSync(file)) await fsp.rm(file);
      return;
    }
  }
  await fsp.writeFile(file, `${JSON.stringify(config, null, 2)}\n`);
}

/** `config.provider` as a mutable object, creating it if absent. */
function providersOf(config: Record<string, unknown>): Record<string, unknown> {
  if (typeof config.provider === 'object' && config.provider !== null) {
    return config.provider as Record<string, unknown>;
  }
  const fresh: Record<string, unknown> = {};
  config.provider = fresh;
  return fresh;
}

/** A provider entry's object, creating it if absent or malformed. */
function providerEntry(
  providers: Record<string, unknown>,
  id: string,
): Record<string, unknown> {
  if (typeof providers[id] === 'object' && providers[id] !== null) {
    return providers[id] as Record<string, unknown>;
  }
  const fresh: Record<string, unknown> = {};
  providers[id] = fresh;
  return fresh;
}

/** A provider entry's `options` object, creating it if absent or malformed. */
function optionsOf(entry: Record<string, unknown>): Record<string, unknown> {
  if (typeof entry.options === 'object' && entry.options !== null) {
    return entry.options as Record<string, unknown>;
  }
  const fresh: Record<string, unknown> = {};
  entry.options = fresh;
  return fresh;
}

function writeHostedApiKey(
  config: Record<string, unknown>,
  provider: string,
  apiToken: string,
): void {
  const entry = providerEntry(providersOf(config), provider);
  optionsOf(entry).apiKey = apiToken;
}

function writeCustomProvider(
  config: Record<string, unknown>,
  parsed: { provider: string; modelId: string },
  {
    npm,
    baseUrl,
    apiToken,
  }: { npm: string; baseUrl: string; apiToken: string },
): void {
  const entry = providerEntry(providersOf(config), parsed.provider);
  entry.npm = npm;
  if (typeof entry.name !== 'string') entry.name = parsed.provider;

  const opts = optionsOf(entry);
  if (baseUrl) opts.baseURL = baseUrl;
  if (apiToken) opts.apiKey = apiToken;

  // Synthesize the single-entry models map from the model id, keeping any
  // models already present (a user/OpenCode could have added others).
  const models =
    typeof entry.models === 'object' && entry.models !== null
      ? (entry.models as Record<string, unknown>)
      : {};
  if (models[parsed.modelId] === undefined) {
    models[parsed.modelId] = { name: parsed.modelId };
  }
  entry.models = models;
}
